import type { FlightOfferSource, FlightSegmentSource } from './flight_offer_source.js';

/**
 * O `offerId` é uma ref opaca `{ sessionId, offerId }` do modal-session, e cada
 * busca cunha uma sessão nova: o MESMO voo físico chega com refs diferentes no
 * card do chat (sessão do agente) e na bancada (sessão da busca de mount). Quem
 * precisa dizer "é o mesmo voo" compara o que não muda entre sessões — os
 * segmentos. Preço, classe tarifária, paradas e política ficam de fora: mudam
 * entre buscas do mesmo voo.
 */
const sameSegment = (a: FlightSegmentSource, b: FlightSegmentSource): boolean =>
  a.carrier === b.carrier &&
  a.flightNumber === b.flightNumber &&
  a.from === b.from &&
  a.to === b.to &&
  a.departAt === b.departAt &&
  a.arriveAt === b.arriveAt;

const sameSegments = (
  a: readonly FlightSegmentSource[],
  b: readonly FlightSegmentSource[],
): boolean =>
  a.length === b.length && a.every((seg, i) => sameSegment(seg, b[i] as FlightSegmentSource));

export function sameFlight(a: FlightOfferSource, b: FlightOfferSource): boolean {
  if (!sameSegments(a.segments, b.segments)) return false;
  if (a.returnLeg === undefined || b.returnLeg === undefined) {
    return a.returnLeg === undefined && b.returnLeg === undefined;
  }
  return sameSegments(a.returnLeg.segments, b.returnLeg.segments);
}

/**
 * A oferta VIVA que corresponde ao pick: por ref exato (pick feito na própria
 * lista), senão por identidade contra a oferta do pick na SEMENTE (pick do host
 * ou feito sobre a semente antes do pouso). `undefined` quando o voo saiu do
 * inventário — o pick continua válido (ref do agente), só não há linha a marcar.
 */
export function liveOfferForPick<O extends FlightOfferSource>(
  pick: { offerId: string } | undefined,
  live: readonly O[],
  seed: readonly FlightOfferSource[],
): O | undefined {
  if (!pick) return undefined;
  const exact = live.find((o) => o.offerId === pick.offerId);
  if (exact) return exact;
  const seeded = seed.find((o) => o.offerId === pick.offerId);
  if (!seeded) return undefined;
  return live.find((o) => sameFlight(o, seeded));
}
